import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';
import useIntakeMedicationPageState from './useIntakeMedicationPageState';

import type { AppDispatch } from '../../../../../../../store';
import type { MedicationPayload } from '../../../../../../common';

export default function useIntakeMedications() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const { medications, hasNoMedications, isSaving, isBulkSaving } = useIntakeMedicationPageState();

  const create = useCallback(
    async (data: MedicationPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeMedication({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeMedication.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (medicationId: number, data: MedicationPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeMedication({
          intakeQuestionnaireId,
          medicationId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeMedication.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (medicationId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeMedication({
          intakeQuestionnaireId,
          medicationId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeMedication.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const clearAll = useCallback(async () => {
    if (!intakeQuestionnaireId) return false;
    const result = await dispatch(
      intakeQuestionnaireActions.deleteAllIntakeMedications({
        intakeQuestionnaireId,
        getAccessTokenSilently,
      }),
    );
    return intakeQuestionnaireActions.deleteAllIntakeMedications.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, intakeQuestionnaireId]);

  const setNoMedications = useCallback(
    async (hasNoMedications: boolean) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.setIntakeHasNoMedications({
          intakeQuestionnaireId,
          hasNoMedications,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.setIntakeHasNoMedications.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    medications,
    hasNoMedications,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoMedications,
  };
}
